import {
  useGetPendingRewardsQuery,
  useApprovePendingRewardsMutation,
} from "@apis/rewards/useRewards"
import type { IPendingRewardDetail } from "@apis/rewards/types"
import { toast } from "sonner"
import { useNavigate, useParams } from "react-router-dom"
import { tableColumnsPendingBatchDetail } from "@pages/Rewards/utils/tableColumnsSchema"
import { DataTable } from "@components/DataTable/DataTable"
import { Button } from "@components/ui/button"
import { PageTitle } from "@components/PageTitle"
import { Loader2, MoveLeft } from "lucide-react"
import { useRowSelect } from "@hooks/useRowSelect"
import { useQueryClient } from "@tanstack/react-query"

export const PendingRewards = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { fulfillment_vendor_code, fulfillment_channel } = useParams() as {
    fulfillment_vendor_code: string
    fulfillment_channel: string
  }

  const { data, isPending, isError, error } = useGetPendingRewardsQuery(
    fulfillment_vendor_code,
    fulfillment_channel,
  )
  const { mutate } = useApprovePendingRewardsMutation()
  const { rowSelection, setRowSelection, getSelectedItems } =
    useRowSelect<IPendingRewardDetail>({
      data: data?.rewards,
      initialSelectAll: true,
    })

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    const rewardsToApprove = getSelectedItems()

    if (rewardsToApprove.length === 0) {
      return toast.error("Please select rewards to approve.")
    }

    mutate(
      {
        fulfillment_vendor_code,
        fulfillment_channel,
        rewards: rewardsToApprove,
      },
      {
        onSuccess: () => {
          toast.success("Pending Rewards were successfully Approved!")
          navigate(`/ops/rewards/${fulfillment_vendor_code}`)
          queryClient.invalidateQueries({ queryKey: ["rewards"] })
        },
        onError: (error) => toast.error(`Error approving notes! ${error}`),
      },
    )
  }

  const rewardStats = data
    ? [
        { label: "Total Rewards", value: data.total_rewards },
        { label: "Avg Amount", value: `$${data.rewards_amount_avg}` },
        { label: "Total Amount", value: `$${data.rewards_amount_sum}` },
      ]
    : []

  if (isPending) {
    return (
      <div className="tw:flex tw:justify-center tw:py-10">
        <Loader2 className="tw:h-6 tw:w-6 tw:animate-spin tw:text-gray-500" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="tw:container tw:mt-6 tw:text-red-500">
        There was an error loading the batch details. Error: {error.message}
      </div>
    )
  }

  return (
    <div className="tw:container tw:flex tw:flex-col tw:gap-6">
      <div>
        <PageTitle title={`Pending Rewards`} className="tw:mb-0!" />
        <Button
          type="button"
          variant="link"
          onClick={() => navigate(`/ops/rewards/${fulfillment_vendor_code}`)}
          className="tw:mb-2!"
        >
          <MoveLeft />
          Return to Vendor
        </Button>
      </div>

      <div>
        <div className="tw:mb-3 tw:flex tw:items-end tw:justify-between tw:rounded-md tw:border tw:border-border tw:bg-white tw:p-4 tw:shadow-2xs">
          <div className="tw:min-w-1/6">
            {rewardStats?.map(({ label, value }) => (
              <p key={label} className="tw:m-0! tw:flex tw:justify-between">
                <span className="tw:font-bold">{label}:</span>
                <span>{value}</span>
              </p>
            ))}
          </div>
          <Button onClick={handleSubmit}>Approve and Submit</Button>
        </div>

        <DataTable
          columns={tableColumnsPendingBatchDetail}
          data={data?.rewards ?? []}
          pending={isPending}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
        />
      </div>
    </div>
  )
}
